import { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { TailSpin } from 'react-loader-spinner';
import { useParams } from 'react-router-dom';

import { BudgetGender } from 'types/api/budgets.type';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { BudgetContext } from 'contexts';

import { useFetch } from 'hooks';

import { checkParams } from 'helpers/checkParams';

import { BudgetListSkeleton } from '../BudgetListSkeleton';
import * as S from './Gender.styles';
import { SingleGender } from './components/SingleGender';

type GenderProps = {
  /** False while the tab is kept mounted but hidden: no infinite-scroll loading then. */
  isActive?: boolean;
};

export function Gender({ isActive = true }: GenderProps): React.ReactElement {
  const { id, gender } = useParams();

  const [displayedItems, setDisplayedItems] = useState<BudgetGender[]>([]);
  const [allGenders, setAllGenders] = useState<BudgetGender[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const { season, zone, marketLine, warehouse } = useAppSelector((state) => state.settings);
  const { params: contextParams } = useContext(BudgetContext);

  const params = {
    season,
    zone,
    seasonLine: marketLine,
    warehouse: warehouse.code,
    budgetNum: id!,
    genderCode: gender!,
    type: contextParams?.type || '',
  };

  const { content, isLoading } = useFetch(
    checkParams(params) ? () => budgetDataService.getGenders(params) : undefined,
    [season, zone, marketLine, warehouse, id, gender, contextParams?.type]
  );

  const ITEMS_PER_PAGE = 20;
  const hasMore = currentIndex < allGenders.length;

  const loadMore = () => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + ITEMS_PER_PAGE;
      const newItems = allGenders.slice(prevIndex, nextIndex);
      setDisplayedItems((prev) => [...prev, ...newItems]);
      return nextIndex;
    });
  };

  const dataMapped = displayedItems?.map((item: BudgetGender) => (
    <SingleGender key={item?.genderCode} data={item} />
  ));

  useEffect(() => {
    if (content) {
      // Convert single object or array to array
      const gendersArray = Array.isArray(content) ? content : [content];
      setAllGenders(gendersArray);
      setDisplayedItems([]);
      setCurrentIndex(0);
    }
  }, [content]);

  useEffect(() => {
    if (allGenders.length > 0 && displayedItems.length === 0 && currentIndex === 0) {
      const initialItems = allGenders.slice(0, ITEMS_PER_PAGE);
      setDisplayedItems(initialItems);
      setCurrentIndex(ITEMS_PER_PAGE);
    }
  }, [allGenders, displayedItems.length, currentIndex]);

  if (isLoading) {
    return <BudgetListSkeleton withImage={false} columns={5} />;
  }

  if (allGenders.length === 0) {
    return <div />;
  }

  return (
    <InfiniteScroll
      hasMore={hasMore && isActive}
      loadMore={loadMore}
      loader={
        <S.LoadingContainer key="loader">
          <TailSpin width="100" color="#286090" />
        </S.LoadingContainer>
      }
    >
      {dataMapped}
    </InfiniteScroll>
  );
}
